import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SharedStream, dashboardApi } from '@/lib/services/dashboard-api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MediaImage } from '@/components/ui/media-image';
import { Video, ArrowRight, Clock, Play, Square, Eye, Users, Settings } from 'lucide-react';
import { formatDistanceToNowSafe } from '@/lib/utils/date';

interface RecentStreamsListProps {
  onViewMore?: () => void;
  maxStreams?: number;
  showActions?: boolean;
}

export function RecentStreamsList({ onViewMore, maxStreams = 5, showActions = false }: RecentStreamsListProps) {
  const navigate = useNavigate();
  const [streams, setStreams] = useState<SharedStream[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRecentStreams();
  }, []);

  const fetchRecentStreams = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await dashboardApi.getRecentStreams(maxStreams);
      setStreams(data);
    } catch (err) {
      console.error('Error fetching recent streams:', err);
      setError('Failed to load recent streams');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'live':
        return 'bg-red-100 text-red-800 hover:bg-red-200';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
      case 'ended':
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  const handleStreamAction = async (streamId: number, action: 'start' | 'stop') => {
    try {
      const success = await dashboardApi.updateStreamStatus(streamId, action);
      if (success) {
        // Refresh the streams list
        await fetchRecentStreams();
      }
    } catch (error) {
      console.error('Error updating stream status:', error);
      toast.error('Failed to update stream status');
    }
  };

  const formatDuration = (duration?: number) => {
    if (!duration) return 'N/A';
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Recent Streams
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(maxStreams)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse">
                <div className="w-12 h-12 bg-gray-200 rounded"></div>
                <div className="flex-1 space-y-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Recent Streams
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchRecentStreams}
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Video className="h-5 w-5" />
          Recent Streams
        </CardTitle>
      </CardHeader>
      <CardContent>
        {streams.length === 0 ? (
          <div className="text-center py-6">
            <Video className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No recent streams</p>
          </div>
        ) : (
          <div className="space-y-3">
            {streams.map((stream) => (
              <div
                key={stream.id}
                onClick={() => navigate(`/streams/${stream.id}`)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50 transition-colors text-left w-full cursor-pointer"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/streams/${stream.id}`);
                  }
                }}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                  {stream.thumbnailUrl ? (
                    <MediaImage
                      src={stream.thumbnailUrl}
                      alt={stream.title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <Video className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium truncate">{stream.title}</p>
                    <Badge
                      variant="secondary"
                      className={`text-xs px-1.5 py-0.5 ${getStatusColor(stream.status)}`}
                    >
                      {stream.status}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {stream.creator && (
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span>{stream.creator.name}</span>
                      </div>
                    )}

                    {stream.viewerCount !== undefined && (
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        <span>{stream.viewerCount}</span>
                      </div>
                    )}

                    {stream.duration && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDuration(stream.duration)}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDistanceToNowSafe(stream.createdAt)}
                    </div>
                  </div>

                  {showActions && (
                    <div className="flex items-center gap-2 mt-2">
                      {stream.status === 'scheduled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStreamAction(stream.id, 'start');
                          }}
                          className="h-7 px-2"
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Start
                        </Button>
                      )}

                      {stream.status === 'live' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStreamAction(stream.id, 'stop');
                          }}
                          className="h-7 px-2"
                        >
                          <Square className="h-3 w-3 mr-1" />
                          Stop
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          // navigate(`/admin/streams/${stream.id}/edit`);
                        }}
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {onViewMore && (
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onViewMore}
                  className="w-full justify-between"
                >
                  Manage All Streams
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
